/**
 * Gunnery scoring, and the records that survive the session.
 *
 * ## What is worth scoring
 *
 * Kills, obviously. But a score built only from kills rewards spraying the whole magazine at
 * everything, so accuracy is scored too — and because "hits ÷ rounds" is a ratio that a single
 * lucky burst can make meaningless, it is applied as a *bonus* on top of the kills rather than as
 * a multiplier that can zero them. The gun and the missile are counted separately for the same
 * reason: four missiles and five hundred rounds are not comparable units and averaging them
 * produces a number that means nothing.
 *
 * The balloon course is scored on **time** instead, because that is what a course is: six
 * balloons, one run, and the clock starts when the first one pops. Timing from the first hit
 * rather than from entering the area is deliberate — otherwise the fastest strategy is to set up
 * the perfect gun pass before you "start", which is not the exercise.
 *
 * ## Persistence
 *
 * One record per course in `localStorage`, behind the project's `StorageService` so a browser
 * that refuses storage simply has no records rather than a page that fails. Everything read back
 * is validated field by field: a hand-edited or half-written record must not be able to put
 * `NaN` on the scoreboard, and `NaN` is exactly what `JSON.parse` will hand you for `null`.
 *
 * `updateFixed` / `updateFrame`: neither. This is called on discrete events — a kill, the end of
 * a run — and never from a hot path.
 */

import { STORAGE_KEYS, readJson, writeJson, type StorageService } from '../core/storage.js';

/** Which exercise a record belongs to. */
export type CourseId = 'drones' | 'balloons';

/** A live run's tally. Plain data, so it can be serialised or sent. */
export interface ScoreState {
  kills: number;
  /** Cannon rounds that connected. */
  cannonHits: number;
  cannonFired: number;
  missileHits: number;
  missilesFired: number;
  /** Wall-clock seconds since the run started. */
  elapsedS: number;
  /** True once the course has been completed. */
  complete: boolean;
}

export function makeScoreState(): ScoreState {
  return {
    kills: 0,
    cannonHits: 0,
    cannonFired: 0,
    missileHits: 0,
    missilesFired: 0,
    elapsedS: 0,
    complete: false,
  };
}

export function resetScore(score: ScoreState): void {
  score.kills = 0;
  score.cannonHits = 0;
  score.cannonFired = 0;
  score.missileHits = 0;
  score.missilesFired = 0;
  score.elapsedS = 0;
  score.complete = false;
}

/** Points per kill, before the accuracy bonus. */
const POINTS_PER_KILL = 1_000;
/** The most the gun's accuracy can add, per kill. */
const MAX_ACCURACY_BONUS = 500;

/**
 * The score for a run.
 *
 * Kills carry it; accuracy tops it up. The gun's contribution is its hit rate against a
 * reference of 25 %, which is a good burst on a manoeuvring target — reaching it earns the full
 * bonus and there is no reward for exceeding it, because past that point the number is measuring
 * how close you got rather than how well you shot.
 */
export function scoreOf(state: ScoreState): number {
  const kills = state.kills * POINTS_PER_KILL;
  if (state.kills === 0) return 0;

  const gunAccuracy = state.cannonFired > 0 ? state.cannonHits / state.cannonFired : 0;
  const missileAccuracy = state.missilesFired > 0 ? state.missileHits / state.missilesFired : 0;
  const shotAtAll = state.cannonFired > 0 || state.missilesFired > 0;
  if (!shotAtAll) return kills;

  const gunShare = Math.min(1, gunAccuracy / 0.25);
  const bonus =
    state.cannonFired > 0 && state.missilesFired > 0
      ? (gunShare + missileAccuracy) * 0.5
      : state.cannonFired > 0
        ? gunShare
        : missileAccuracy;
  return Math.round(kills + bonus * MAX_ACCURACY_BONUS * state.kills);
}

/** What is kept between sessions. */
export interface CourseRecord {
  /** Best score. */
  bestScore: number;
  /** Best completion time [s]. `null` until the course has been completed once. */
  bestTimeS: number | null;
  /** How many times it has been completed. */
  completions: number;
}

export type RecordBook = Record<CourseId, CourseRecord>;

export function emptyRecordBook(): RecordBook {
  return {
    drones: { bestScore: 0, bestTimeS: null, completions: 0 },
    balloons: { bestScore: 0, bestTimeS: null, completions: 0 },
  };
}

/**
 * Keeps the record book, and writes it when something is actually beaten.
 *
 * Writes only on an improvement rather than at the end of every run: `localStorage` is
 * synchronous and on a quota-limited store the write that matters is the one you want to succeed.
 */
export class RecordKeeper {
  private readonly storage: StorageService;
  private book: RecordBook = emptyRecordBook();

  constructor(storage: StorageService) {
    this.storage = storage;
  }

  get records(): Readonly<RecordBook> {
    return this.book;
  }

  recordFor(course: CourseId): Readonly<CourseRecord> {
    return this.book[course];
  }

  load(): void {
    const stored = readJson(this.storage, STORAGE_KEYS.gunneryRecords, isRecordBookish);
    if (stored === null) return;
    this.book = validate(stored);
  }

  /**
   * File a completed run.
   *
   * @returns what, if anything, was beaten — so the UI can say "NEW BEST" about the right thing.
   */
  submit(
    course: CourseId,
    state: ScoreState,
  ): { newBestScore: boolean; newBestTime: boolean } {
    const record = this.book[course];
    const score = scoreOf(state);
    let newBestScore = false;
    let newBestTime = false;

    if (score > record.bestScore) {
      record.bestScore = score;
      newBestScore = true;
    }
    if (state.complete) {
      record.completions++;
      if (record.bestTimeS === null || state.elapsedS < record.bestTimeS) {
        record.bestTimeS = state.elapsedS;
        newBestTime = true;
      }
    }
    if (newBestScore || newBestTime || state.complete) this.save();
    return { newBestScore, newBestTime };
  }

  /** Wipe the scoreboard. Offered in the menu, because somebody always wants to. */
  reset(): void {
    this.book = emptyRecordBook();
    this.storage.remove(STORAGE_KEYS.gunneryRecords);
  }

  private save(): void {
    writeJson(this.storage, STORAGE_KEYS.gunneryRecords, this.book);
  }
}

function isRecordBookish(value: unknown): value is Partial<Record<CourseId, unknown>> {
  return typeof value === 'object' && value !== null;
}

/**
 * Rebuild a record book from whatever came off disk, field by field.
 *
 * Anything missing, mistyped or non-finite falls back to the empty record. The `Number.isFinite`
 * checks are not paranoia about hand-editing: `JSON.parse('{"bestScore":null}')` yields `null`,
 * `null > 0` is `false` but `Number(null)` is `0`, and a half-written record is exactly what a
 * tab closed mid-write leaves behind.
 */
function validate(stored: Partial<Record<CourseId, unknown>>): RecordBook {
  const book = emptyRecordBook();
  for (const course of ['drones', 'balloons'] as const) {
    const raw = stored[course];
    if (typeof raw !== 'object' || raw === null) continue;
    const entry = raw as Partial<CourseRecord>;
    const record = book[course];
    if (typeof entry.bestScore === 'number' && Number.isFinite(entry.bestScore) && entry.bestScore >= 0) {
      record.bestScore = Math.round(entry.bestScore);
    }
    if (typeof entry.bestTimeS === 'number' && Number.isFinite(entry.bestTimeS) && entry.bestTimeS > 0) {
      record.bestTimeS = entry.bestTimeS;
    }
    if (typeof entry.completions === 'number' && Number.isFinite(entry.completions) && entry.completions >= 0) {
      record.completions = Math.round(entry.completions);
    }
  }
  return book;
}

/** `m:ss.t`, for the HUD and the menu. */
export function formatTime(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const minutes = Math.floor(clamped / 60);
  const rest = clamped - minutes * 60;
  return `${minutes}:${rest.toFixed(1).padStart(4, '0')}`;
}
